import { useQueries, useQuery } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";

// ---------------------------------------------------------------------------
// User-name resolution hooks.
//
// Resolves a raw `DOMAIN\username` (or bare `username`) to its display name
// via the DM API `/lookups/user-name` endpoint. Backed by the mock WebSec2
// roster server-side, falling back to RP_USER_PROFILE — when nothing
// matches, the API echoes the raw id back as `fullName`, so the SPA can
// always render *something*.
//
// Two variants:
//   - `useUserName(userId)` for single-record callers (e.g. the deal header
//     owner chip)
//   - `useUserNamesBatch(userIds[])` for grids — uses `useQueries` to
//     parallelise + share react-query cache with the single-record hook so
//     a grid render that includes the deal owner doesn't double-fetch.
//
// The endpoint is keyed by the qualified id, cached server-side for an
// hour. We mirror that on the client with `staleTime: 1h` so the same id
// rendered across pages stays a single network round-trip per session.
// ---------------------------------------------------------------------------

export interface UserNameResult {
  /** The exact id the caller passed in (preserved verbatim for round-trip). */
  userId: string;
  /** Display name. Always populated — falls back to `userId` when unresolved. */
  fullName: string;
  firstName: string | null;
  lastName: string | null;
}

const BASE = "/dm/V1/lookups";
const STALE_MS = 60 * 60 * 1000; // 1h — matches the API cache

const qkSingle = (id: string) => ["user-name", id.toLowerCase()] as const;

/**
 * Resolve a single user id to a display name. Returns the raw id back as
 * `fullName` when unresolved, so callers can always render `.data?.fullName
 * ?? userId` without an additional null check.
 *
 * Disabled when `userId` is null/blank — react-query's `enabled` flag keeps
 * the network quiet for rows that have no actor.
 */
export const useUserName = (userId: string | null | undefined) =>
  useQuery({
    queryKey: qkSingle(userId ?? ""),
    enabled: !!userId && userId.trim().length > 0,
    staleTime: STALE_MS,
    queryFn: async (): Promise<UserNameResult> => {
      const response = await apiClient.get<UserNameResult>(`${BASE}/user-name`, {
        params: { userId },
      });
      return response.data;
    },
  });

/**
 * Resolve many user ids in parallel. Each id is its own react-query entry
 * — the single-record hook (`useUserName`) reads from the same cache so a
 * page can mix both without double-fetching.
 *
 * Implementation note: for batches > ~10 unique ids we could call the
 * dedicated `/user-names` batch endpoint, but the per-id cache + dedup is
 * already efficient enough in practice (a 50-row grid with 8 distinct
 * actors costs 8 requests on first render, 0 on the next page within the
 * 1h window). Leaving the batch endpoint as a future optimisation hook.
 */
export const useUserNamesBatch = (userIds: ReadonlyArray<string | null | undefined>) => {
  const unique = Array.from(
    new Set(
      userIds
        .map((u) => (u ?? "").trim())
        .filter((u) => u.length > 0)
        .map((u) => u),
    ),
  );

  const queries = useQueries({
    queries: unique.map((id) => ({
      queryKey: qkSingle(id),
      staleTime: STALE_MS,
      queryFn: async (): Promise<UserNameResult> => {
        const response = await apiClient.get<UserNameResult>(`${BASE}/user-name`, {
          params: { userId: id },
        });
        return response.data;
      },
    })),
  });

  // Map id → resolved name (or undefined while loading). Case-insensitive
  // lookups so callers don't have to normalise.
  const byUserId = new Map<string, UserNameResult | undefined>();
  unique.forEach((id, i) => byUserId.set(id.toLowerCase(), queries[i].data));

  return {
    /** Lookup helper: returns the display name (or the raw id) for one user. */
    nameFor: (userId: string | null | undefined): string => {
      if (!userId || userId.trim().length === 0) return "—";
      const hit = byUserId.get(userId.trim().toLowerCase());
      return hit?.fullName ?? userId;
    },
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
};
